import React from 'react';
import { Card } from '../ui/Card';
import { CHAIN_CONFIGS } from '../../utils/constants';
import { BridgeTransaction } from '../../types';
import { getExplorerTxUrl } from '../../utils/web3';

interface TransactionHistoryProps {
  transactions: BridgeTransaction[];
  onClearHistory?: () => void;
  onRefresh?: () => void; // Add refresh callback
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ 
  transactions, 
  onClearHistory,
  onRefresh
}) => {
  // No need for logging anymore

  // Show a message when there are no transactions, but still show the refresh button
  const hasTransactions = transactions.length > 0;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status: string, tx: BridgeTransaction) => {
    switch (status) {
      case 'pending':
        return (
          <div className="flex flex-col">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-900/40 text-yellow-300 backdrop-blur-sm border border-yellow-700/30 shadow-sm shadow-yellow-600/10">
              Pending
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Waiting for confirmation
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Estimated time: 2-10 minutes
            </span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex flex-col">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/40 text-blue-300 backdrop-blur-sm border border-blue-700/30 shadow-sm shadow-blue-600/10 animate-pulse">
              Processing
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Cross-chain transfer in progress
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Estimated time: 2-10 minutes
            </span>
          </div>
        );
      case 'completed':
        return (
          <div className="flex flex-col">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-300 backdrop-blur-sm border border-green-700/30 shadow-sm shadow-green-600/10">
              Completed
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Funds received on {CHAIN_CONFIGS[tx.toChain]?.name || tx.toChain}
            </span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex flex-col">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-300 backdrop-blur-sm border border-red-700/30 shadow-sm shadow-red-600/10">
              Failed
            </span>
            <span className="text-xs text-gray-400 mt-1">
              Transaction could not be completed
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card 
      title="Transaction History" 
      subtitle="Your recent bridge transactions"
      className="mt-8"
      action={
        onClearHistory && (
          <button
            onClick={onClearHistory}
            className="text-sm text-red-400 hover:text-red-300 transition-all duration-200 px-2 py-1 rounded-md bg-red-900/10 hover:bg-red-900/20 backdrop-blur-sm border border-red-800/20"
            title="Clear transaction history"
          >
            Clear History
          </button>
        )
      }
    >
      {/* Refresh button */}
      {onRefresh && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={onRefresh}
            className="text-sm text-primary-400 hover:text-primary-300 transition-all duration-200 px-3 py-1.5 rounded-md bg-primary-900/20 hover:bg-primary-800/30 backdrop-blur-sm border border-primary-800/20 flex items-center"
            title="Refresh transaction history"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      )}
      
      {hasTransactions ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700/30 backdrop-blur-sm">
          <thead className="bg-gray-800/30">
            <tr className="border-b border-gray-700/20">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                From → To
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Token
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/30">
            {transactions.map((tx) => (
              <tr key={tx.hash} className="hover:bg-gray-700/30 transition-colors duration-200">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                  {formatDate(tx.timestamp)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                  <div className="flex items-center">
                    <span>{CHAIN_CONFIGS[tx.fromChain]?.name || tx.fromChain}</span>
                    <svg className="h-4 w-4 mx-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span>{CHAIN_CONFIGS[tx.toChain]?.name || tx.toChain}</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                  {tx.token}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                  {tx.amount} {tx.token}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {getStatusBadge(tx.status, tx)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <div className="flex flex-col space-y-1">
                    <a
                      href={getExplorerTxUrl(CHAIN_CONFIGS[tx.fromChain]?.chainId || '', tx.hash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:text-primary-400 transition-colors duration-200 px-2 py-1 rounded-md bg-primary-900/20 hover:bg-primary-800/30 backdrop-blur-sm text-center"
                    >
                      Source Tx
                    </a>
                    {tx.status === 'completed' && tx.toChainTxHash && (
                      <a
                        href={getExplorerTxUrl(CHAIN_CONFIGS[tx.toChain]?.chainId || '', tx.toChainTxHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-500 hover:text-green-400 transition-colors duration-200 px-2 py-1 rounded-md bg-green-900/20 hover:bg-green-800/30 backdrop-blur-sm text-center"
                      >
                        Dest Tx
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : (
        <div className="py-8 text-center">
          <p className="text-gray-400 mb-4">No transactions yet. Bridge some tokens to get started!</p>
        </div>
      )}
    </Card>
  );
};
